// HSE — Lista lavoratori con riepilogo documenti e scadenze
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { User, AlertTriangle, CheckCircle, ChevronRight, FileText } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import api from '../../lib/api'
import type { Certification } from '../../hooks/useHse'

interface Technician {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  avatarUrl?: string
}

function useWorkersSummary() {
  return useQuery({
    queryKey: ['hse', 'lavoratori'],
    queryFn: async () => {
      const [techRes, certRes] = await Promise.all([
        api.get<Technician[]>('/technicians'),
        api.get<Certification[]>('/hse/certifications'),
      ])
      const technicians = techRes.data ?? []
      const certs = certRes.data ?? []
      const now = new Date()

      return technicians.map((t) => {
        const mine = certs.filter((c) => c.technicianId === t.id)
        const expired = mine.filter(
          (c) => c.expiryDate && new Date(c.expiryDate) < now,
        ).length
        const expiring = mine.filter((c) => {
          if (!c.expiryDate) return false
          const exp = new Date(c.expiryDate)
          const soon = new Date(exp.getTime() - 30 * 86400000)
          return exp >= now && soon <= now
        }).length
        return { ...t, totalCerts: mine.length, expired, expiring }
      })
    },
  })
}

export function LavoratoriPage() {
  const navigate = useNavigate()
  const { data = [], isLoading } = useWorkersSummary()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lavoratori"
        subtitle="Documenti HSE per tecnico"
        breadcrumb={[{ label: 'HSE', href: '/hse' }, { label: 'Lavoratori' }]}
      />

      {isLoading && (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && data.length === 0 && (
        <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-10 text-center text-sm text-[var(--fc-foreground-muted)]">
          Nessun tecnico trovato.
        </div>
      )}

      <ul className="space-y-2">
        {data.map((t) => (
          <li key={t.id}>
            <button
              onClick={() => navigate(`/hse/lavoratori/${t.id}`)}
              className="w-full flex items-center gap-4 rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] px-5 py-4 text-left hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
            >
              {/* Avatar */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--fc-primary)_12%,transparent)]">
                {t.avatarUrl ? (
                  <img src={t.avatarUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <User className="h-5 w-5 text-[var(--fc-primary)]" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[var(--fc-foreground)] truncate">
                  {t.firstName} {t.lastName}
                </p>
                <p className="text-xs text-[var(--fc-foreground-muted)] truncate">{t.email}</p>
              </div>

              {/* Stato documenti */}
              <div className="flex items-center gap-2 shrink-0">
                {t.expired > 0 && (
                  <Badge variant="danger" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t.expired} scadut{t.expired === 1 ? 'o' : 'i'}
                  </Badge>
                )}
                {t.expiring > 0 && (
                  <Badge variant="warning" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t.expiring} in scadenza
                  </Badge>
                )}
                {t.expired === 0 && t.expiring === 0 && t.totalCerts > 0 && (
                  <Badge variant="success" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    OK
                  </Badge>
                )}
                <span className="flex items-center gap-1 text-xs text-[var(--fc-foreground-muted)]">
                  <FileText className="h-3.5 w-3.5" />
                  {t.totalCerts}
                </span>
                <ChevronRight className="h-4 w-4 text-[var(--fc-foreground-subtle)]" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
